/* eslint-disable react-refresh/only-export-components -- the tab list and its helpers live together on purpose */
/**
 * The Portfolio tabs and their panels (#788), shared by the Portfolio page and the mini portfolio under Markets (#2242).
 * Both show the same components; the data comes from the user session held above the routes (#1809), so showing them
 * in a second place reads and subscribes nothing more.
 */
import type { ReactNode } from 'react';
import BalancesPanel from './BalancesPanel';
import PositionsPanel from './PositionsPanel';
import { OpenOrdersSlot, OrderHistorySlot, TradesSlot } from './OrderSlots';
import { SettledPanel } from '../settlement/ui';
import type { PlainKey } from '../i18n/i18n';
import { useT } from '../i18n/useT';

export const PORTFOLIO_TABS = [
  { id: 'balances', label: 'portfolio.tab.balances' },
  { id: 'positions', label: 'portfolio.tab.positions' },
  { id: 'orders', label: 'portfolio.tab.orders' },
  { id: 'history', label: 'portfolio.tab.history' },
  { id: 'trades', label: 'portfolio.tab.trades' },
  { id: 'settled', label: 'portfolio.tab.settled' },
] as const satisfies readonly { id: string; label: PlainKey }[];
export type PortfolioTabId = (typeof PORTFOLIO_TABS)[number]['id'];

export function isPortfolioTab(v: string | null | undefined): v is PortfolioTabId {
  return PORTFOLIO_TABS.some((t) => t.id === v);
}

export interface PortfolioTabListProps {
  tab: PortfolioTabId;
  onSelect: (id: PortfolioTabId) => void;
  /** Prefix of the tab and panel ids ("portfolio" on the page, "mini-portfolio" under Markets). */
  idPrefix: string;
  /** Smaller tabs (mini portfolio). */
  small?: boolean;
  className?: string;
}

export function PortfolioTabList({ tab, onSelect, idPrefix, small = false, className = '' }: PortfolioTabListProps) {
  const t = useT();
  return (
    <div role="tablist" aria-label={t('portfolio.sections')} className={className}>
      <div className={`flex w-max rounded-lg bg-surface-2 ${small ? 'gap-0.5 p-0.5' : 'gap-1 p-1'}`}>
        {PORTFOLIO_TABS.map((pt) => (
          <button
            key={pt.id}
            type="button"
            role="tab"
            id={`${idPrefix}-tab-${pt.id}`}
            aria-selected={tab === pt.id}
            aria-controls={`${idPrefix}-panel-${pt.id}`}
            onClick={() => onSelect(pt.id)}
            className={`whitespace-nowrap rounded-md ${small ? 'px-2.5 py-1 text-xs' : 'px-3 py-1.5 text-sm'} ${
              tab === pt.id ? 'bg-surface font-medium text-fg' : 'text-muted hover:text-fg'
            }`}
          >
            {t(pt.label)}
          </button>
        ))}
      </div>
    </div>
  );
}

function TabPanel({ id, idPrefix, children }: { id: PortfolioTabId; idPrefix: string; children: ReactNode }) {
  return (
    <div role="tabpanel" id={`${idPrefix}-panel-${id}`} aria-labelledby={`${idPrefix}-tab-${id}`}>
      {children}
    </div>
  );
}

/** The active tab's panel. Only that one is mounted, as on the page: a hidden tab costs nothing. */
export function PortfolioTabPanel({ tab, address, idPrefix }: { tab: PortfolioTabId; address: string; idPrefix: string }) {
  const panel =
    tab === 'balances' ? (
      <BalancesPanel address={address} />
    ) : tab === 'positions' ? (
      <PositionsPanel address={address} />
    ) : tab === 'orders' ? (
      <OpenOrdersSlot address={address} />
    ) : tab === 'history' ? (
      <OrderHistorySlot address={address} />
    ) : tab === 'trades' ? (
      <TradesSlot address={address} />
    ) : (
      <SettledPanel address={address} />
    );
  return (
    <TabPanel id={tab} idPrefix={idPrefix}>
      {panel}
    </TabPanel>
  );
}
